import { jsPDF } from "jspdf";
import axios from "axios";
import { drawFreshEarthSeal } from "./pdfLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brand palette (matches roiPdf.js)
const FOREST = [26, 43, 34];
const TERRA = [184, 101, 81];
const BONE = [247, 246, 240];
const INK = [28, 28, 28];
const SLATE = [92, 94, 85];
const STONE = [138, 140, 130];

function fmtSlot(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${suffix} IST`;
}

async function fetchImageDataUrl(path, token) {
  const res = await axios.get(`${API}/files/${path}`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: "blob",
  });
  const blob = res.data;
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ dataUrl: reader.result, type: blob.type });
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Generate a one-click printable site-visit report PDF for a lead. */
export async function generateLeadReportPdf(lead, token) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = 595;
  const H = 842;
  const M = 48;

  // --- Header band ---
  doc.setFillColor(...FOREST);
  doc.rect(0, 0, W, 120, "F");
  drawFreshEarthSeal(doc, { x: M, y: 28, size: 64, tone: "light" });
  doc.setTextColor(...BONE);
  doc.setFont("times", "italic");
  doc.setFontSize(24);
  doc.text("Fresh Earth", M + 76, 60);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(220, 224, 214);
  doc.text("OED — Odour Eliminator & Decomposer", M + 76, 78);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...TERRA);
  doc.text("SITE REPORT", W - M, 52, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setTextColor(220, 224, 214);
  doc.text(
    new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
    W - M,
    68,
    { align: "right" }
  );

  // --- Title ---
  let y = 168;
  doc.setTextColor(...INK);
  doc.setFont("times", "normal");
  doc.setFontSize(26);
  doc.text("Pilot enquiry", M, y);
  y += 26;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...SLATE);
  doc.text(`Received ${new Date(lead.created_at).toLocaleString("en-IN")}`, M, y);

  // --- Contact block ---
  y += 32;
  doc.setDrawColor(210, 210, 200);
  doc.line(M, y, W - M, y);
  y += 20;

  const rows = [
    ["Name", lead.name || "—"],
    ["Email", lead.email || "—"],
    ["Phone", lead.phone || "—"],
    ["Organization", lead.organization || "—"],
    ["Segment", lead.segment || "—"],
  ];
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...STONE);
  doc.text("CONTACT", M, y);
  y += 14;
  doc.setFontSize(11);
  rows.forEach(([k, v]) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...STONE);
    doc.text(k, M, y);
    doc.setTextColor(...INK);
    const wrap = doc.splitTextToSize(String(v), W - M - M - 130);
    doc.text(wrap, M + 130, y);
    y += 14 * Math.max(1, wrap.length);
  });

  // --- Callback block ---
  if (lead.callback_date || lead.callback_slot) {
    y += 14;
    doc.setDrawColor(210, 210, 200);
    doc.line(M, y, W - M, y);
    y += 18;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...STONE);
    doc.text("REQUESTED CALLBACK", M, y);
    y += 16;
    doc.setFont("times", "normal");
    doc.setFontSize(18);
    doc.setTextColor(...FOREST);
    doc.text(
      `${lead.callback_date || "—"}  ·  ${fmtSlot(lead.callback_slot) || "—"}`,
      M,
      y
    );
  }

  // --- Message ---
  if (lead.message) {
    y += 26;
    doc.setDrawColor(210, 210, 200);
    doc.line(M, y, W - M, y);
    y += 18;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...STONE);
    doc.text("MESSAGE", M, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...INK);
    const msg = doc.splitTextToSize(lead.message, W - M - M);
    doc.text(msg, M, y);
    y += msg.length * 14;
  }

  // --- Photos ---
  const photos = lead.photos || [];
  if (photos.length > 0) {
    y += 22;
    if (y > H - 260) { doc.addPage(); y = M; }
    doc.setDrawColor(210, 210, 200);
    doc.line(M, y, W - M, y);
    y += 18;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...STONE);
    doc.text(`SITE PHOTOS (${photos.length})`, M, y);
    y += 12;

    const cols = 2;
    const gap = 12;
    const cellW = (W - M * 2 - gap) / cols;
    const cellH = cellW * 0.72; // 3:2 aspect-ish

    for (let i = 0; i < photos.length; i += 1) {
      const p = photos[i];
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = M + col * (cellW + gap);
      const yy = y + row * (cellH + gap) + 10;

      // Ensure page-break
      if (yy + cellH > H - M) {
        doc.addPage();
        y = M;
      }

      try {
        const { dataUrl, type } = await fetchImageDataUrl(p.storage_path, token);
        const fmt =
          type.includes("png") ? "PNG" :
          type.includes("webp") ? "WEBP" :
          type.includes("gif") ? "GIF" :
          "JPEG";
        doc.addImage(dataUrl, fmt, x, yy, cellW, cellH, undefined, "FAST");
      } catch {
        // draw a placeholder box
        doc.setFillColor(245, 244, 235);
        doc.rect(x, yy, cellW, cellH, "F");
        doc.setTextColor(...STONE);
        doc.setFontSize(9);
        doc.text("Photo unavailable", x + 10, yy + 18);
      }
      doc.setDrawColor(220, 220, 210);
      doc.rect(x, yy, cellW, cellH);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...STONE);
      const cap = p.original_filename || `Photo ${i + 1}`;
      doc.text(doc.splitTextToSize(cap, cellW)[0], x, yy + cellH + 10);
    }
  }

  // --- Footer ---
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p += 1) {
    doc.setPage(p);
    doc.setDrawColor(210, 210, 200);
    doc.line(M, H - 50, W - M, H - 50);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...STONE);
    doc.text("Fresh Earth · fresh-earth.com · care@fresh-earth.com · +91 98995 34141", M, H - 34);
    doc.text(`Page ${p} of ${totalPages}`, W - M, H - 34, { align: "right" });
  }

  const safeName = (lead.name || "lead").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  doc.save(`fresh-earth-site-report-${safeName}.pdf`);
}
